import {
  QueryObserverLoadingErrorResult,
  QueryObserverPendingResult,
  QueryObserverRefetchErrorResult,
  QueryObserverSuccessResult,
  UseQueryResult,
} from "@tanstack/react-query";
import { Center } from "../ui/layout";
import { Heading } from "../ui/typography";
import { Spinner } from "../ui/Spinner";

type JSXElementOrNull = React.JSX.Element | null;

type ErrorResult<TError> =
  | QueryObserverLoadingErrorResult<unknown, TError>
  | QueryObserverRefetchErrorResult<unknown, TError>;

interface CreateQueryGateOptions<TError> {
  /**
   * Default error handler
   */
  error: (query: ErrorResult<TError>) => JSXElementOrNull;
  /**
   * Default pending handler
   */
  pending: (query: QueryObserverPendingResult<unknown, TError>) => JSXElementOrNull;
}

interface QueryGateOptions<TError> {
  queries: Array<UseQueryResult<unknown, TError>>;
  /**
   * Optionally override default error handling
   */
  error?: (query: ErrorResult<TError>) => JSXElementOrNull;
  /**
   * Optionally override pending state
   */
  pending?: (query: QueryObserverPendingResult<unknown, TError>) => JSXElementOrNull;
}

interface QueryGateOptionsNoEmpty<TError> extends QueryGateOptions<TError> {
  success: (query: Array<QueryObserverSuccessResult<unknown, TError>>) => JSXElementOrNull;
}

export function createQueryGate<TError>(queryCellOpts: CreateQueryGateOptions<TError>) {
  function QueryGate(opts: QueryGateOptionsNoEmpty<TError>): JSXElementOrNull;
  function QueryGate(opts: QueryGateOptionsNoEmpty<TError>) {
    const { queries: queries } = opts;

    for (const query of queries) {
      if (query.status === "pending") {
        return queryCellOpts.pending(query);
      }
      if (query.status === "error") {
        return opts.error?.(query) ?? queryCellOpts.error(query);
      }
    }

    return opts.success(queries as Array<QueryObserverSuccessResult<unknown, TError>>);

    // impossible state
    return null;
  }
  return QueryGate;
}

export const QueryGate = createQueryGate({
  error: () => <Heading>Error loading</Heading>,
  pending: () => (
    <Center css={{ w: "100%", h: 64 }}>
      <Spinner />
    </Center>
  ),
});
